/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from '@/hooks/use-navigate';
import { redirect, useActionData } from 'react-router';
import type { Route } from './+types/_empty.login';
import { Link } from '@/components/link';
import { routes } from '@/route-paths';
import { SeoMeta } from '@/components/seo-meta';
import { buildCanonicalUrl } from '@/utils/canonical-url';
import { useTranslation } from 'react-i18next';
import PasswordlessLoginForm from '@/components/login/passwordless-login-form';
import OtpModal from '@/components/login/otp-modal';
import { LoginGuestWishlistBanner } from '@/components/login/login-guest-wishlist-banner';
import { SocialLoginButtons } from '@/components/buttons/social-login-buttons';
import { isAbsoluteURL, getSafeReturnUrl } from '@/lib/utils';
import loginLogoM from '/images/login-logo-m.png';
import loginHero from '/images/login-hero.png';
import { getAppOrigin } from '@/lib/origin';
import { getConfig, useConfig } from '@salesforce/storefront-next-runtime/config';
import { getLoginPreferences } from '@salesforce/storefront-next-runtime/data-store';
import { getTranslation } from '@salesforce/storefront-next-runtime/i18n';
import { buildUrlFromContext } from '@/lib/url.server';
import { TurnstileWidget } from '@/components/security/turnstile-widget';
import { getTurnstileSiteKey, getTurnstileMode, isTurnstileEnabled } from '@/lib/turnstile/utils';

// services
import {
    getAuth,
    authorizePasswordless,
    getPasswordLessAccessToken,
    updateAuth as updateAuthServer,
} from '@/middlewares/auth.server';
import { authorizeIDP } from '@/lib/api/auth/social-login.server';
import { EnterpriseLoginButton } from '@/components/login/enterprise-login-button';
import { mergeBasket } from '@/lib/api/basket.server';
import {
    appendWishlistMergeFlag,
    captureGuestWishlistSnapshot,
    mergeWishlist,
    type WishlistMergeResult,
} from '@/lib/api/wishlist.server';
import { getPasswordlessErrorMessageKey, extractErrorMessage } from '@/lib/auth/error-handler';
import { getLogger } from '@/lib/logger.server';
import { enforceTurnstile } from '@/lib/turnstile/enforce.server';

type LoginActionResponse = {
    success: boolean;
    error?: string;
    showOTPForm?: boolean;
    email?: string;
};

type LoginLoaderData = {
    error?: string;
    passwordlessSent?: boolean;
    email?: string;
    mode: string;
    isPasswordlessLoginEnabled: boolean;
    isSocialLoginEnabled: boolean;
    isEnterpriseLoginEnabled: boolean;
    isEnterpriseLoginReady: boolean;
    enterpriseProviderLabel: string;
    returnUrl?: string | null;
    action?: string | null;
    actionParams?: string | null;
    showOTPForm?: boolean;
    otpLength?: number;
    pageUrl: string;
    /** Number of items in the guest wishlist (0 if guest has none, no list, or SCAPI failed). */
    guestWishlistCount: number;
};

export async function loader({ request, context }: Route.LoaderArgs) {
    const logger = getLogger(context);
    const session = getAuth(context);
    const url = new URL(request.url);
    const pageUrl = buildCanonicalUrl(url.origin, url.pathname, url.search);
    const returnUrl = getSafeReturnUrl(url.searchParams.get('returnUrl'));

    // If user is already logged in as registered user, redirect to returnUrl or home
    const { accessToken, accessTokenExpiry, userType, customerId } = session;
    if (
        accessToken &&
        typeof accessTokenExpiry === 'number' &&
        accessTokenExpiry >= Date.now() &&
        userType === 'registered' &&
        customerId
    ) {
        logger.debug('Login: already authenticated, redirecting');
        return redirect(buildUrlFromContext(returnUrl || routes.home, context));
    }

    const passwordlessSent = url.searchParams.get('passwordless') === 'sent';
    const showOTPForm = url.searchParams.get('otp') === 'true';
    const email = url.searchParams.get('email');

    const tokenFromUrl = url.searchParams.get('token');
    const legacyOtpCode = url.searchParams.get('otpCode');
    const token = tokenFromUrl ?? legacyOtpCode;
    const pendingAction = url.searchParams.get('action');
    const actionParams = url.searchParams.get('actionParams');
    const error = url.searchParams.get('error');

    const config = getConfig(context);
    // To enable passwordless login, the "Enable Email Verification" site preference under "Storefront Login Preferences" must be enabled.
    const { emailVerificationEnabled } = getLoginPreferences(context);
    const isPasswordlessLoginEnabled = Boolean(emailVerificationEnabled);
    const isSocialLoginEnabled = Boolean(config.features.socialLogin?.enabled);
    const isEnterpriseLoginEnabled = Boolean(config.features.enterpriseLogin?.enabled);
    const isEnterpriseLoginReady = Boolean(config.features.enterpriseLogin?.enabled && config.features.enterpriseLogin?.idpHint);
    const enterpriseProviderLabel = config.features.enterpriseLogin?.providerLabel ?? 'Enterprise';
    const mode = url.searchParams.get('mode') || (isPasswordlessLoginEnabled ? 'passwordless' : 'password');

    // Auto-verify OTP token if both email and token are provided in URL
    if (email && token) {
        // Snapshot the guest wishlist BEFORE the SLAS swap; the registered token can't authorize a read against the guest customerId.
        const guestWishlistSnapshot = await captureGuestWishlistSnapshot(context);

        try {
            const tokenResponse = await getPasswordLessAccessToken(context, token);

            // Update session with auth data. userType, customerId, usid, and the refresh-token
            // expiry cap all derive from the access-token JWT inside updateAuth — no follow-up
            // call is needed.
            updateAuthServer(context, tokenResponse);

            // Merge guest basket with registered user basket
            try {
                await mergeBasket(context);
            } catch (basketError) {
                logger.error('Login: basket merge failed during passwordless login', { error: basketError });
            }

            let wishlistMergeResult: WishlistMergeResult | null = null;
            if (guestWishlistSnapshot) {
                try {
                    wishlistMergeResult = await mergeWishlist(context, guestWishlistSnapshot);
                } catch (wishlistError) {
                    logger.error('Login: wishlist merge failed during passwordless login', { error: wishlistError });
                }
            }

            logger.info('Login: passwordless verification succeeded');
            const target = buildUrlFromContext(returnUrl || routes.home, context);
            if (wishlistMergeResult) {
                const { url: redirectUrl, setCookie } = appendWishlistMergeFlag(context, target, wishlistMergeResult);
                return redirect(redirectUrl, { headers: { 'Set-Cookie': setCookie } });
            }
            return redirect(target);
        } catch (verifyError) {
            // Auto-verification failed - show error with OTP form
            logger.warn('Login: passwordless auto-verification failed');
            const errorMessage = extractErrorMessage(verifyError);
            const errorKey = getPasswordlessErrorMessageKey(errorMessage);
            const { t } = getTranslation(context);

            return {
                error: t(errorKey),
                showOTPForm: true,
                email,
                mode,
                isPasswordlessLoginEnabled,
                isSocialLoginEnabled,
                isEnterpriseLoginEnabled,
                isEnterpriseLoginReady,
                enterpriseProviderLabel,
                returnUrl,
                action: pendingAction,
                actionParams,
                otpLength: config.auth.otpLength,
                pageUrl,
                guestWishlistCount: guestWishlistSnapshot?.items.length ?? 0,
            };
        }
    }

    // Drives the guest-saved-items banner above the form. Helper gates on guest userType
    // and swallows SCAPI errors — a missing snapshot must not block sign-in.
    const guestWishlistSnapshot = await captureGuestWishlistSnapshot(context);

    return {
        error,
        passwordlessSent,
        showOTPForm,
        email,
        mode,
        isPasswordlessLoginEnabled,
        isSocialLoginEnabled,
        isEnterpriseLoginEnabled,
        isEnterpriseLoginReady,
        enterpriseProviderLabel,
        returnUrl,
        action: pendingAction,
        actionParams,
        otpLength: config.auth.otpLength,
        pageUrl,
        guestWishlistCount: guestWishlistSnapshot?.items.length ?? 0,
    };
}

/**
 * Server action for authentication. Login is handled server-side for security (httpOnly cookies,
 * PKCE code verifier storage) and integration with SLAS.
 *
 * Returns `LoginActionResponse | Response` because the action has two distinct outcomes:
 * - `Response` (redirect) — successful login or social IDP authorization. React Router intercepts
 *   the redirect before it reaches the component, so `useActionData()` never sees it.
 * - `LoginActionResponse` — errors or intermediate states (e.g., OTP form). This data is
 *   serialized and delivered to the component via `useActionData()` for rendering.
 */
export async function action({ request, context }: Route.ActionArgs): Promise<LoginActionResponse | Response> {
    const logger = getLogger(context);
    const config = getConfig(context);
    const { t } = getTranslation(context);
    const genericError = t('errors:genericTryAgain');

    try {
        const formData = await request.formData();

        const email = formData.get('email')?.toString();
        const loginMode = formData.get('loginMode')?.toString();
        const provider = formData.get('provider')?.toString();
        const redirectPath = formData.get('redirectPath')?.toString();
        const isSocialLoginEnabled = Boolean(config.features.socialLogin?.enabled);

        if (loginMode === 'enterprise') {
            // Enterprise SSO flow — Azure Enterprise Application via SLAS external IdP
            const enterpriseConfig = config.features.enterpriseLogin;
            if (!enterpriseConfig?.enabled || !enterpriseConfig.idpHint) {
                logger.warn('Login: enterprise login attempted but idpHint not configured');
                return { success: false, error: genericError };
            }
            const callbackUri = enterpriseConfig.callbackUri ?? config.features.socialLogin.callbackUri;
            const enterpriseRedirectURI = isAbsoluteURL(callbackUri)
                ? callbackUri
                : `${getAppOrigin(context)}${buildUrlFromContext(callbackUri, context)}`;
            const enterpriseResult = await authorizeIDP(context, {
                hint: enterpriseConfig.idpHint,
                redirectURI: enterpriseRedirectURI,
            });
            if (enterpriseResult.success && enterpriseResult.redirectUrl) {
                logger.info('Login: enterprise redirect initiated');
                const resp = redirect(enterpriseResult.redirectUrl);
                if (redirectPath) {
                    resp.headers.append('Set-Cookie', `cc-idp-rp=${encodeURIComponent(redirectPath)}; Path=/; HttpOnly; SameSite=Lax; Max-Age=300`);
                }
                return resp;
            }
            logger.warn('Login: enterprise authorization failed');
            return { success: false, error: genericError };
        } else if (loginMode === 'social') {
            // Social login flow
            if (!provider || !isSocialLoginEnabled) {
                return { success: false, error: genericError };
            }
            const socialCallback = config.features.socialLogin.callbackUri;
            const socialLoginRedirectURI = isAbsoluteURL(socialCallback)
                ? socialCallback
                : `${getAppOrigin(context)}${buildUrlFromContext(socialCallback, context)}`;
            const result = await authorizeIDP(context, {
                hint: provider,
                redirectURI: socialLoginRedirectURI,
            });
            if (result.success && result.redirectUrl) {
                logger.info('Login: social redirect initiated', { provider });
                const resp = redirect(result.redirectUrl);
                if (redirectPath) {
                    resp.headers.append('Set-Cookie', `cc-idp-rp=${encodeURIComponent(redirectPath)}; Path=/; HttpOnly; SameSite=Lax; Max-Age=300`);
                }
                return resp;
            }
            logger.warn('Login: social authorization failed', { provider });
            return { success: false, error: genericError };
        } else if (loginMode === 'passwordless') {
            // Passwordless login flow
            if (!email) {
                return { success: false, error: genericError };
            }

            const turnstileToken = formData.get('turnstileToken')?.toString();
            const allowed = await enforceTurnstile({
                request,
                config,
                turnstileToken,
                logger,
                actionName: 'login-passwordless',
                email,
            });
            if (!allowed) {
                return { success: false, error: t('errors:api.forbidden') };
            }

            // Build redirectPath from returnUrl, action, and actionParams for passwordless flow
            const url = new URL(request.url);
            const returnUrl = getSafeReturnUrl(url.searchParams.get('returnUrl'));
            const pendingAction = url.searchParams.get('action');
            const actionParams = url.searchParams.get('actionParams');

            // Construct redirectPath with all params encoded
            let finalRedirectPath = redirectPath || returnUrl || routes.home;
            if (returnUrl && (pendingAction || actionParams)) {
                const redirectParams = new URLSearchParams();
                if (pendingAction) {
                    redirectParams.set('action', pendingAction);
                }
                if (actionParams) {
                    redirectParams.set('actionParams', actionParams);
                }
                const queryString = redirectParams.toString();
                finalRedirectPath = queryString ? `${returnUrl}?${queryString}` : returnUrl;
            }

            try {
                await authorizePasswordless(context, { userid: email, redirectPath: finalRedirectPath });
                logger.info('Login: passwordless code sent');
                // Passwordless authorization sent - redirect to login page with OTP form
                const params = new URLSearchParams();
                params.set('otp', 'true');
                params.set('email', email);
                if (returnUrl) {
                    params.set('returnUrl', returnUrl);
                }
                if (pendingAction) {
                    params.set('action', pendingAction);
                }
                if (actionParams) {
                    params.set('actionParams', actionParams);
                }
                return { success: true, showOTPForm: true, email };
            } catch (error) {
                const errorMessage = extractErrorMessage(error);
                const errorKey = getPasswordlessErrorMessageKey(errorMessage);

                return { success: false, error: t(errorKey) };
            }
        }

        return { success: false, error: genericError };
    } catch {
        return { success: false, error: genericError };
    }
}

const MV_DARK = '#29282E';
const MV_FG = '#F8F6FD';
const MV_MUTED = '#8E8C93';
const MV_PURPLE = '#635EE2';
const MV_LINK = '#9D96FF';

/** CSS variable overrides scoped to the left panel so all child components render in dark mode */
const darkPanelVars: React.CSSProperties = {
    ['--background' as string]: MV_DARK,
    ['--foreground' as string]: MV_FG,
    ['--card' as string]: MV_DARK,
    ['--card-foreground' as string]: MV_FG,
    ['--muted' as string]: 'rgba(248,246,253,0.05)',
    ['--muted-foreground' as string]: MV_MUTED,
    ['--primary' as string]: MV_PURPLE,
    ['--primary-foreground' as string]: '#FFFFFF',
    ['--secondary' as string]: 'rgba(248,246,253,0.08)',
    ['--secondary-foreground' as string]: MV_FG,
    ['--accent' as string]: 'rgba(248,246,253,0.08)',
    ['--accent-foreground' as string]: MV_FG,
    ['--border' as string]: 'rgba(248,246,253,0.4)',
    ['--border-subtle' as string]: 'rgba(248,246,253,0.15)',
    ['--input' as string]: MV_DARK,
    ['--ring' as string]: MV_PURPLE,
    ['--popover' as string]: '#3a3840',
    ['--popover-foreground' as string]: MV_FG,
};

function LoginBrand() {
    return (
        <div className="flex items-center gap-2 mb-auto pb-12">
            <img src={loginLogoM} alt="" aria-hidden="true" className="h-8 w-8 object-contain" />
            <span className="text-sm font-semibold tracking-wide" style={{ color: MV_FG }}>Multiverse Store</span>
        </div>
    );
}

function LoginFooter() {
    return (
        <div className="mt-auto pt-12 text-xs" style={{ color: MV_MUTED }}>
            Problems getting in?{' '}
            <a href="mailto:mvm-support@wizards.com" className="hover:opacity-80 transition-opacity" style={{ color: MV_LINK }}>
                Contact us
            </a>
        </div>
    );
}

function LoginHeroPanel() {
    return (
        <div className="hidden lg:block flex-1 relative overflow-hidden" style={{ background: MV_DARK }}>
            <img
                src={loginHero}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 w-full h-full object-cover object-center"
            />
        </div>
    );
}

export default function Login({ loaderData }: { loaderData: LoginLoaderData }): ReactElement {
    const { t } = useTranslation('login');
    const actionData = useActionData<typeof action>();
    const navigate = useNavigate();
    const config = useConfig();

    const {
        error: loaderError,
        passwordlessSent,
        email,
        isSocialLoginEnabled,
        isEnterpriseLoginEnabled,
        isEnterpriseLoginReady,
        enterpriseProviderLabel,
        returnUrl,
        showOTPForm,
        otpLength,
        pageUrl,
        guestWishlistCount,
    } = loaderData;

    const [resendTurnstileToken, setResendTurnstileToken] = useState<string | null>(null);
    const resendTurnstileResetRef = useRef<(() => void) | null>(null);

    const turnstileEnabled = config ? isTurnstileEnabled(config) : false;
    const turnstileMode = config ? getTurnstileMode(config) : 'managed';
    const turnstileSiteKey = useMemo(() => {
        if (!config || !turnstileEnabled) return null;
        if (typeof window !== 'undefined') {
            const baseUrl = `${window.location.protocol}//${window.location.host}`;
            return getTurnstileSiteKey(config, baseUrl);
        }
        return null;
    }, [config, turnstileEnabled]);

    const handleResendTurnstileSuccess = useCallback((token: string) => {
        setResendTurnstileToken(token);
    }, []);
    const handleResendTurnstileError = useCallback(() => {
        setResendTurnstileToken(null);
    }, []);
    const handleResendTurnstileExpire = useCallback(() => {
        setResendTurnstileToken(null);
    }, []);
    // Prefer actionData error (from form submission) over loaderData error (from URL params)
    const error = actionData?.error || loaderError || undefined;

    // Check if we should show OTP form from actionData (after email submission) or loaderData (from URL)
    const shouldShowOTPForm = actionData?.showOTPForm || showOTPForm;
    const showOTPModal = Boolean(shouldShowOTPForm);
    const otpEmail = actionData?.email || email;

    // Show passwordless success state
    if (passwordlessSent && email) {
        return (
            <div className="min-h-screen flex" style={{ background: MV_DARK }}>
                <div
                    className="flex flex-col min-h-screen px-12 lg:px-16 py-10 w-full lg:w-[686px] lg:flex-shrink-0"
                    style={darkPanelVars}>
                    <LoginBrand />
                    <div className="flex-1 flex flex-col justify-center max-w-[296px] w-full space-y-6">
                        <div className="space-y-4">
                            <h2 className="text-2xl font-semibold" style={{ color: MV_FG }}>{t('checkEmailTitle')}</h2>
                            <p className="text-sm" style={{ color: MV_MUTED }}>{t('checkEmailDescription', { email })}</p>
                            <Link
                                to={routes.login}
                                className="inline-flex items-center text-sm hover:opacity-80"
                                style={{ color: MV_LINK }}>
                                {t('backToLogin')}
                            </Link>
                        </div>
                    </div>
                    <LoginFooter />
                </div>
                <LoginHeroPanel />
            </div>
        );
    }

    const renderForm = () => (
        <PasswordlessLoginForm
            error={error}
            redirectPath={returnUrl || undefined}
        />
    );

    return (
        <>
            <SeoMeta
                title={t('meta.title', { defaultValue: 'Sign In' })}
                description={t('meta.description', {
                    defaultValue: 'Sign in to your account to view orders, manage your wishlist, and more.',
                })}
                openGraph={{ type: 'website', url: pageUrl }}
            />
            <div className="min-h-screen flex" style={{ background: MV_DARK }}>
                {/* Left panel */}
                <div
                    className="flex flex-col min-h-screen px-12 lg:px-16 py-10 w-full lg:w-[686px] lg:flex-shrink-0"
                    style={darkPanelVars}>
                    <LoginBrand />

                    {/* Form area */}
                    <div className="flex-1 flex flex-col justify-center max-w-[296px] w-full space-y-6">
                        <div>
                            <h1 className="text-[2rem] font-bold leading-tight" style={{ color: MV_FG }}>
                                Welcome, Stranger!
                            </h1>
                            <p className="mt-1 text-sm" style={{ color: MV_MUTED }}>
                                Say &ldquo;Friend&rdquo; and enter.
                            </p>
                        </div>

                        {isEnterpriseLoginEnabled && isEnterpriseLoginReady ? (
                            <>
                                {error && <p className="text-sm text-destructive">{error}</p>}
                                <EnterpriseLoginButton
                                    providerLabel={enterpriseProviderLabel}
                                    redirectPath={returnUrl}
                                />
                            </>
                        ) : (
                            <>
                                <LoginGuestWishlistBanner count={guestWishlistCount} />
                                {renderForm()}
                                {isSocialLoginEnabled ? <SocialLoginButtons /> : null}
                            </>
                        )}
                    </div>

                    <LoginFooter />
                </div>

                {/* Right hero — desktop only */}
                <LoginHeroPanel />
            </div>

            {/* Turnstile widget for OTP resend — hidden, generates tokens for the resend fetch */}
            {showOTPModal && turnstileEnabled && turnstileSiteKey && (
                <TurnstileWidget
                    siteKey={turnstileSiteKey}
                    onSuccess={handleResendTurnstileSuccess}
                    onError={handleResendTurnstileError}
                    onExpire={handleResendTurnstileExpire}
                    enabled={turnstileEnabled}
                    mode={turnstileMode}
                    resetRef={resendTurnstileResetRef}
                />
            )}

            {/* OTP Modal - appears over the login page */}
            <OtpModal
                isOpen={showOTPModal}
                otpLength={otpLength}
                initialError={loaderError}
                onClose={() => {
                    // Navigate back to login without OTP modal
                    const url = new URL(window.location.href);
                    url.searchParams.delete('otp');
                    url.searchParams.delete('error');
                    url.searchParams.delete('token');
                    url.searchParams.delete('email');
                    window.history.replaceState({}, '', url.toString());
                    window.location.reload();
                }}
                email={otpEmail || ''}
                onSuccess={(_tokenResponse, meta) => {
                    // Redirect to return URL or home after successful login.
                    // Forward the wishlist merge flag so the destination route can render a toast.
                    const target = returnUrl || routes.home;
                    if (meta?.wishlistMerge) {
                        const separator = target.includes('?') ? '&' : '?';
                        void navigate(`${target}${separator}wishlistMerge=${meta.wishlistMerge}`);
                    } else {
                        void navigate(target);
                    }
                }}
                onResendCode={async () => {
                    const formData = new FormData();
                    formData.append('email', otpEmail || '');
                    formData.append('loginMode', 'passwordless');
                    if (returnUrl) {
                        formData.append('redirectPath', returnUrl);
                    }
                    if (resendTurnstileToken) {
                        formData.append('turnstileToken', resendTurnstileToken);
                    }
                    await fetch(window.location.pathname, {
                        method: 'POST',
                        body: formData,
                    });
                    if (turnstileEnabled) {
                        setResendTurnstileToken(null);
                        resendTurnstileResetRef.current?.();
                    }
                }}
            />
        </>
    );
}
